<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSubjectsStore } from '@/stores/subjects'
import SubjectSelector from '@/components/subject/SubjectSelector.vue'
import SubjectDetailPanel from '@/components/subject/SubjectDetailPanel.vue'
import AddSubjectModal from '@/components/subject/AddSubjectModal.vue'
import FosButton from '@/components/ui/FosButton.vue'

const route = useRoute()
const incidentId = route.params.id as string
const subjectsStore = useSubjectsStore()

const showAddModal = ref(false)

const headingText = computed(() =>
  subjectsStore.subjects.length === 1 ? 'Subject' : 'Subjects',
)

const statText = computed(() => {
  const count = subjectsStore.subjects.length
  if (count === 0) return 'No subjects registered'
  if (count === 1) {
    const s = subjectsStore.subjects[0]!
    const parts: string[] = []
    if (s.subject_category) parts.push(s.subject_category)
    if (s.age) parts.push(`Age ${s.age}`)
    return parts.length ? parts.join(' · ') : '1 subject registered'
  }
  return `${count} subjects registered`
})
</script>

<template>
  <div class="h-full flex flex-col overflow-hidden">
    <div class="flex items-end justify-between px-6 pt-4 border-b border-border shrink-0">
      <div class="pb-3">
        <h2 class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">{{ headingText }}</h2>
        <p class="font-mono text-xs text-outline mt-0.5">{{ statText }}</p>
      </div>
      <SubjectSelector
        v-if="subjectsStore.subjects.length > 1"
        :subjects="subjectsStore.subjects"
        :active-subject-id="subjectsStore.activeSubjectId"
        @add="showAddModal = true"
      />
      <div v-else class="pb-3">
        <FosButton variant="tertiary" size="sm" @click="showAddModal = true">+ Add Subject</FosButton>
      </div>
    </div>

    <AddSubjectModal
      v-model:visible="showAddModal"
      :incident-id="incidentId"
      @created="(id) => subjectsStore.setActiveSubject(id)"
    />

    <div v-if="subjectsStore.loading" class="flex-1 flex items-center justify-center text-outline">
      <div class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin mr-2" />
      Loading…
    </div>

    <!-- Empty state -->
    <div
      v-else-if="subjectsStore.subjects.length === 0"
      class="flex-1 flex flex-col items-center justify-center gap-4 text-on-surface-variant"
    >
      <span class="material-symbols-outlined text-5xl text-outline">person_search</span>
      <p class="font-headline font-bold uppercase tracking-tight">No subjects yet</p>
      <p class="text-sm text-outline font-mono">Add the missing person to begin tracking their information.</p>
      <FosButton variant="primary" @click="showAddModal = true">
        <span class="material-symbols-outlined text-[16px]">person_add</span>
        Add Subject
      </FosButton>
    </div>

    <!-- Subject panel -->
    <div v-else class="flex-1 overflow-y-auto">
      <SubjectDetailPanel
        v-if="subjectsStore.activeSubject"
        :subject="subjectsStore.activeSubject"
        :incident-id="incidentId"
      />
    </div>
  </div>
</template>
